import { cx } from 'utils';

import Spinner from './Spinner';

/**
 * Button — primary interactive primitive for the LearnX design system.
 *
 * Flat, website-style: solid brand surfaces, hairline rings, simple color
 * transitions on hover. Fully reusable via `variant`, `size`, `loading`,
 * `fullWidth`, and icon slots. Polymorphic through `as` (e.g. render as a
 * router `Link` or `a`).
 */
const VARIANTS = {
  // Solid indigo fill — the primary action. Flat, minimal: no gradient, no
  // heavy shadow.
  primary: 'bg-brand-600 text-white hover:bg-brand-700',
  // Clean white surface + neutral hairline — the default secondary (matches
  // Linear/Stripe/Vercel). For an indigo-outlined secondary, use `outline`.
  // slate-300 ring, not slate-200: on white a #E2E8F0 hairline is ~1.2:1 and
  // the button reads as floating text rather than a control.
  secondary:
    'bg-white text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 hover:text-slate-900 hover:ring-slate-400',
  // Deep slate — dark action.
  navy: 'bg-navy-900 text-white hover:bg-navy-800',
  // Indigo hairline outline + indigo text — the "colored secondary".
  outline:
    'border border-brand-300 bg-white text-brand-700 hover:bg-brand-50 hover:border-brand-400',
  // Quiet — toolbars, list rows.
  ghost: 'bg-transparent text-slate-700 hover:bg-slate-100 hover:text-slate-900',
  // Sky — secondary emphasis.
  teal: 'bg-teal-500 font-semibold text-white hover:bg-teal-600',
  // Gold accent.
  accent: 'bg-gold-500 font-semibold text-white hover:bg-gold-600',
  // Destructive.
  danger: 'bg-danger text-white hover:bg-danger/90',
};

const SIZES = {
  xs: 'h-8 px-3 text-xs gap-1.5 rounded-lg',
  sm: 'h-9 px-3.5 text-sm gap-1.5 rounded-xl',
  md: 'h-10 px-4.5 text-sm gap-2 rounded-xl',
  lg: 'h-11 px-6 text-base gap-2 rounded-xl',
  xl: 'h-12 px-7 text-base gap-2.5 rounded-xl',
  // Square icon-only buttons.
  icon: 'h-10 w-10 rounded-xl',
};

export default function Button({
  as: Component = 'button',
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  disabled,
  children,
  ...props
}) {
  const isDisabled = disabled || loading;
  const small = size === 'xs' || size === 'sm';
  // White spinner on solid coloured fills; brand spinner on light surfaces.
  const spinnerTone = ['primary', 'navy', 'teal', 'accent', 'danger'].includes(variant) ? 'white' : 'brand';

  const classes = cx(
    // `lx-btn` is a hook for scoped skins (e.g. the student shell's pill shape).
    'lx-btn group relative inline-flex select-none items-center justify-center font-medium',
    'transition-all duration-200 ease-premium focus-ring',
    'disabled:pointer-events-none disabled:opacity-50',
    VARIANTS[variant] || VARIANTS.primary,
    SIZES[size] || SIZES.md,
    fullWidth && 'w-full',
    className
  );

  return (
    <Component
      className={classes}
      disabled={Component === 'button' ? isDisabled : undefined}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      {...props}
    >
      {loading && <Spinner size={small ? 14 : 18} tone={spinnerTone} className="-ml-0.5" />}
      {!loading && leftIcon}
      {children && <span className={cx(loading && 'opacity-80')}>{children}</span>}
      {!loading && rightIcon}
    </Component>
  );
}
